import {CAMPAIGN_KEYS} from "@/api/campaignApi";
import {COMPANY_KEYS} from "@/api/companyApi";
import WarningIcon from "@/assets/colorable/warning.svg?react";
import Modal from "@/components/Common/Modal";
import {client} from "@/httpClient";
import {capturePostHogEvent, PostHogEvents} from "@/posthog";
import useCampaignFilterStore from "@/store/campaignFilter";
import {confirmCampaignEndingModalAtom} from "@/store/jotaiAtoms";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useAtom} from "jotai";
import {type FC} from "react";
import {useTranslation} from "react-i18next";

const ConfirmCampaignEndingModal: FC = () => {
  const {t} = useTranslation("campaign_application");
  const [modalData, setModalData] = useAtom(confirmCampaignEndingModalAtom);
  const campaignListSearchParams = useCampaignFilterStore(state => state.searchParams);
  const queryClient = useQueryClient();

  const {mutate: completed} = useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: CAMPAIGN_KEYS.mCampaignOngoingComplete(modalData.campaignId!),
    mutationFn: () => client.post(CAMPAIGN_KEYS.mCampaignOngoingComplete(modalData.campaignId!)[0]),
    onSuccess: async () => {
      const companyId = modalData.companyId;

      if (modalData.campaignId && companyId) {
        await Promise.all(
          [
            CAMPAIGN_KEYS.qCampaign(modalData.campaignId),
            CAMPAIGN_KEYS.qCampaignList(campaignListSearchParams),
            COMPANY_KEYS.qCompanyCampaigns({companyId, status: "active"}),
            COMPANY_KEYS.qCompanyCampaigns({companyId, status: "paused"}),
            COMPANY_KEYS.qCompanyCampaigns({companyId, status: "complete"}),
          ].map(queryKey => queryClient.invalidateQueries({queryKey})),
        );

        capturePostHogEvent(PostHogEvents.CampaignCompleted, {campaignId: modalData.campaignId, companyId});
      }
    },
  });

  return (
    <Modal
      backgroundClassName="md:w-fit"
      confirmButtonClassName="flex-3"
      confirmButtonText={t("confirm_campaign_ending")}
      onClose={() => setModalData({isOpen: false, campaignId: null, companyId: null})}
      onConfirm={completed}
      modalTitle={t("confirm_campaign_ending_title")}>
      <span className="text-neutral-black text-sm">{t("confirm_campaign_ending_description")}</span>
      <div className="bg-warning-light-yellow border-warning-dark-yellow text-warning-dark-yellow flex gap-2 rounded-md border px-3 py-4">
        <WarningIcon className="size-4" />
        <span className="text-xs">{t("confirm_campaign_ending_disclaimer")}</span>
      </div>
    </Modal>
  );
};

export default ConfirmCampaignEndingModal;
